import { useEffect } from 'react';
import { createUseStyles } from 'react-jss';
import { useTranslation } from 'react-i18next';

import { ListHeading } from '../ListHeading';
import type { LcpnTheme } from '../../styles/theme';
import { Scrollbar } from '../Scrollbar';
import { useMapData } from '../../map-data/use-map-data';
import { useHeatMapSelectedLocations } from '../HeatMapExperience/HeatMapSelectedLocationsProvider';
import { useHeatMapFilterOptions } from '../../map-data/use-heat-map-filter-options';
import { useUpdateNavigationBar } from '../NavigationBar/NavigationBarProvider';

import { CountryRowAccordion } from './CountryRowAccordion';
import { COUNTRY_LIST_ID } from './constants';
import { getHeatmapCountryListScrollContainer } from './get-heatmap-country-list-scroll-container';

const useStyles = createUseStyles((theme: LcpnTheme) => ({
  countryListHeatMap: {
    height: '93.5%',
  },

  heading: {
    backgroundColor: theme.colors['blue-10'],
    padding: theme.spacing(1, 0),
    borderTop: `${theme.colors['grey-100']} solid 2px`,
    color: theme.colors['blue-100'],

    '& > div > p': {
      marginLeft: `clamp(${theme.spacing(8)}, 5.5%, ${theme.spacing(8)})`,
    },
    '& > div:first-child > p': {
      display: 'none',
    },
  },

  headingActive: {
    color: theme.colors.white,
    backgroundColor: theme.colors['blue-100'],
    borderTop: `${theme.colors['blue-100']} solid 2px`,
  },

  subHeading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    color: theme.colors['blue-100'],
    marginLeft: theme.spacing(8),
    padding: theme.spacing(4, 16, 4, 0),
  },

  mobileHeading: {
    display: 'none',
  },

  level: {
    display: 'none',
  },

  subHeadingGroup: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginLeft: theme.spacing(6),
    marginRight: theme.spacing(6),
    padding: theme.spacing(1, 0, 1, 0),
  },

  [`@media screen and (max-width: ${theme.breakPoints.tablet}px)`]: {
    heading: {
      '&&': {
        display: 'flex',
        alignItems: 'center',
        '& > div > p': {
          marginLeft: 0,
        },
        '& > div:first-child > p': {
          display: 'block',
          marginLeft: theme.spacing(4),
        },
      },
    },
    link: {
      '&&': {
        display: 'none',
      },
    },
    level: {
      '&&': {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        color: theme.colors['blue-100'],
        borderBottom: `solid 1px ${theme.colors['grey-100']}`,
        padding: theme.spacing(2, 1, 2, 0),
        '& > div > p': {
          marginLeft: theme.spacing(4),
        },
      },
    },
    subHeading: {
      '&&': {
        marginTop: theme.spacing(4),
        marginLeft: theme.spacing(4),
        padding: theme.spacing(4, 1, 4, 0),
        '& > div > p': {
          fontSize: theme.fontSizes.p3,
          color: theme.colors['blue-100'],
        },
      },
    },
    subHeadingGroup: {
      '&&': {
        '& > div > p': {
          marginLeft: theme.spacing(-4),
        },
        '& > img': {
          display: 'none',
        },
      },
    },
  },
}));

export function HeatMapCountryList() {
  const classes = useStyles();
  const { t } = useTranslation();

  const { loading, countryHeatMapMetaData } = useMapData();
  const {
    filterOptions,
    resetFilterOptions,
    filteredCountryMetaData: countryMetaCollection,
  } = useHeatMapFilterOptions();
  const { resetNavigationBar, updateSelectedNavigationItem } =
    useUpdateNavigationBar();

  const hasFiltersApplied = Object.keys(filterOptions).length > 0;

  const {
    setSelectedCountryCode,
    selectedCountryCode,
    setShowCountryCard,
    resetExperience,
  } = useHeatMapSelectedLocations();

  // TODO: Confirm this code is no longer required and remove
  //
  // const countryMetaCollection = useMemo(() => {
  //   const countryMetaData = countryHeatMapMetaData
  //     ? Object.values(countryHeatMapMetaData)
  //     : [];
  //
  //   return countryMetaData.filter(metaData => {
  //     // Status filtering with OR logic (multiple statuses allowed)
  //     if (filterOptions.status) {
  //       const statusArray = Array.isArray(filterOptions.status)
  //         ? filterOptions.status
  //         : [filterOptions.status];
  //
  //       // If no statuses selected, show all countries
  //       if (statusArray.length === 0) {
  //         // Continue to continent filter
  //       } else {
  //         // Check if country status matches any of the selected statuses
  //         const countryStatusKey = headMapStatusMap[metaData.countryStatus];
  //         const matchesStatus = statusArray.includes(countryStatusKey);
  //
  //         if (!matchesStatus) {
  //           return false; // Exclude this country
  //         }
  //       }
  //     }
  //
  //     // Continent filtering
  //     if (
  //       filterOptions.continent &&
  //       metaData.continent !== filterOptions.continent
  //     ) {
  //       return false; // Exclude this country
  //     }
  //
  //     return true; // Include this country
  //   });
  // }, [countryHeatMapMetaData, filterOptions]);

  // handle navigation via address bar query strings
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const qsId = params.get('heatmapId');
    if (qsId?.length === 2) {
      setSelectedCountryCode(qsId);
    }
  }, [setSelectedCountryCode]);

  const handleReset = () => {
    getHeatmapCountryListScrollContainer()?.scrollTo({
      behavior: 'smooth',
      top: 0,
    });
    resetExperience();
    updateSelectedNavigationItem('map');
    resetFilterOptions();
  };

  if (loading || !countryHeatMapMetaData) {
    // handle loading here
    return null;
  }

  const handleCountryClick = (countryCode: string) => {
    const active = selectedCountryCode === countryCode;
    // toggle visibility of country information
    setSelectedCountryCode(active ? '' : countryCode);
    setShowCountryCard(active ? false : true);
    resetNavigationBar();
  };

  return (
    <div className={classes.countryListHeatMap}>
      <ListHeading
        navigationAction={() => {
          handleReset();
        }}
        navigationLabel={t('dataViewCountries.reset')}
        navigationVisible={!!selectedCountryCode || hasFiltersApplied}
        label={t('dataViewCountries.counter', {
          replace: {
            count: countryMetaCollection.length,
          },
        })}
      />
      <Scrollbar>
        <ul id={COUNTRY_LIST_ID}>
          {countryMetaCollection?.map((countryMetaData, index) => (
            <CountryRowAccordion
              active={
                selectedCountryCode === countryMetaData.countryIso3166_1_2Name
              }
              record={countryMetaData}
              key={
                countryMetaData.countryName +
                '_' +
                countryMetaData.countryIso3166_1_2Name
              }
              handleClick={() =>
                handleCountryClick(countryMetaData.countryIso3166_1_2Name)
              }
              countryIndex={index}
            />
          ))}
        </ul>
      </Scrollbar>
    </div>
  );
}
